import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ColorPicker from "../components/ColorPicker";
import LogoUploader from "../components/LogoUploader";
import QRPreview from "../components/QRPreview";
import { createQRCode, getQRCode, updateQRCode } from "../services/api";

const DEFAULT_FORM = {
  title: "",
  destination_url: "",
  qr_color: "#000000",
  background_color: "#FFFFFF",
  qr_design: "square",
  logo_enabled: false,
  logo_size: 20,
  logo_radius: 8,
  logo_shape: "square",
};

const DESIGNS = [
  { value: "square", label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "dots", label: "Dots" },
];

const SHAPES = [
  { value: "square", label: "Square" },
  { value: "rounded", label: "Rounded" },
  { value: "circle", label: "Circle" },
];

function Section({ number, title, hint, children }) {
  return (
    <div className="form-section">
      <div className="section-head">
        <span className="section-number">{number}</span>

        <div>
          <div className="section-title">{title}</div>
          {hint && <div className="section-hint">{hint}</div>}
        </div>
      </div>

      {children}
    </div>
  );
}

export default function CreateQR() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditing) {
      getQRCode(id).then(({ data }) => {
        setForm({ ...DEFAULT_FORM, ...data });

        if (data.logo_image) {
          setLogoPreviewUrl(data.logo_image);
        }
      });
    }
  }, [id, isEditing]);

  const update = (key) => (value) =>
    setForm({ ...form, [key]: value });

  const handleLogoSelect = (file) => {
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setForm({ ...form, logo_enabled: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError("");

    try {
      const payload = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (key === "logo_image") return;
        payload.append(key, value);
      });

      if (logoFile) {
        payload.append("logo_image", logoFile);
      }

      if (isEditing) {
        await updateQRCode(id, payload);
      } else {
        await createQRCode(payload);
      }

      navigate("/my-qr-codes");
    } catch (err) {
      setError(
        "Could not save the QR code. Please check the fields and try again."
      );

      setSaving(false);
    }
  };

  return (
    <div>
      {isEditing && (
        <div className="edit-back-wrapper">
          <button
            type="button"
            className="edit-back-button"
            onClick={() => navigate(-1)}
          >
            <span className="back-arrow">←</span>
            <span>Back</span>
          </button>
        </div>
      )}

      <div className="page-title">
        {isEditing ? "Edit QR Code" : "Create QR Code"}
      </div>

      

      <div className="page-subtitle">
        Enter a destination URL and customize how your QR code looks
      </div>

      <div className="grid grid-2 create-layout">

        {/* FORM */}
        <form
          className="card form-card"
          onSubmit={handleSubmit}
          style={{ position: "relative" }}
        >

          {saving && (
            <div className="form-saving-overlay">
              <span className="form-saving-spinner" />

              <span className="form-saving-text">
                {isEditing
                  ? "Saving changes..."
                  : "Generating your QR code..."}
              </span>
            </div>
          )}

          {/* Disabling the fieldset locks every input/button inside it */}
          <fieldset disabled={saving} className="form-fieldset">

            {/* 1. DETAILS */}
            <Section
              number="1"
              title="Details"
              hint="Where should the QR code lead?"
            >
              <div className="form-group">
                <label className="form-label">
                  Title
                </label>

                <input
                  className="form-input"
                  placeholder="e.g. Summer Campaign"
                  value={form.title}
                  onChange={(e) =>
                    update("title")(e.target.value)
                  }
                  required
                />
              </div>

              <div
                className="form-group"
                style={{ marginBottom: 0 }}
              >
                <label className="form-label">
                  Destination URL
                </label>

                <input
                  type="url"
                  className="form-input"
                  placeholder="https://example.com"
                  value={form.destination_url}
                  onChange={(e) =>
                    update("destination_url")(e.target.value)
                  }
                  required
                />
              </div>
            </Section>

            {/* 2. STYLE */}
            <Section
              number="2"
              title="Style"
              hint="Pick colors and a pattern"
            >
              <div
                className="grid grid-2"
                style={{ gap: 16 }}
              >
                <ColorPicker
                  label="QR Color"
                  value={form.qr_color}
                  onChange={update("qr_color")}
                />

                <ColorPicker
                  label="Background Color"
                  value={form.background_color}
                  onChange={update("background_color")}
                />
              </div>

              <label
                className="form-label"
                style={{ marginTop: 16 }}
              >
                QR Design
              </label>

              <div className="design-options">
                {DESIGNS.map((d) => (
                  <button
                    type="button"
                    key={d.value}
                    className={`design-tile${
                      form.qr_design === d.value
                        ? " selected"
                        : ""
                    }`}
                    onClick={() =>
                      update("qr_design")(d.value)
                    }
                  >
                    <span
                      className={`design-glyph glyph-${d.value}`}
                    >
                      {Array.from({ length: 9 }).map(
                        (_, i) => (
                          <i key={i} />
                        )
                      )}
                    </span>

                    {d.label}
                  </button>
                ))}
              </div>
            </Section>

            {/* 3. LOGO */}
            <Section
              number="3"
              title="Logo"
              hint="Optional: place your logo in the center"
            >
              <div className="switch-row">
                <div>
                  <div className="switch-title">
                    Show logo in QR code
                  </div>

                  <div className="switch-sub">
                    Uploading a logo turns this on automatically
                  </div>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={form.logo_enabled}
                  className={`switch${
                    form.logo_enabled ? " on" : ""
                  }`}
                  onClick={() =>
                    update("logo_enabled")(
                      !form.logo_enabled
                    )
                  }
                >
                  <span className="switch-knob" />
                </button>
              </div>

              <LogoUploader
                onFileSelect={handleLogoSelect}
                previewUrl={logoPreviewUrl}
              />

              {form.logo_enabled && (
                <div className="logo-controls">

                  {/* LOGO SIZE */}
                  <div className="form-group">
                    <div className="range-head">
                      <label
                        className="form-label"
                        style={{ margin: 0 }}
                      >
                        Logo Size
                      </label>

                      <span className="range-value">
                        {form.logo_size}%
                      </span>
                    </div>

                    <input
                      type="range"
                      min="10"
                      max="35"
                      className="range-input"
                      value={form.logo_size}
                      onChange={(e) =>
                        update("logo_size")(
                          Number(e.target.value)
                        )
                      }
                    />
                  </div>

                  {/* LOGO SHAPE */}
                  <div className="form-group">
                    <label className="form-label">
                      Logo Shape
                    </label>

                    <div className="segmented">
                      {SHAPES.map((s) => (
                        <button
                          type="button"
                          key={s.value}
                          className={
                            form.logo_shape === s.value
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            update("logo_shape")(s.value)
                          }
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* CORNER RADIUS */}
                  {form.logo_shape === "rounded" && (
                    <div
                      className="form-group"
                      style={{ marginBottom: 0 }}
                    >
                      <div className="range-head">
                        <label
                          className="form-label"
                          style={{ margin: 0 }}
                        >
                          Corner Radius
                        </label>

                        <span className="range-value">
                          {form.logo_radius}px
                        </span>
                      </div>

                      <input
                        type="range"
                        min="0"
                        max="30"
                        className="range-input"
                        value={form.logo_radius}
                        onChange={(e) =>
                          update("logo_radius")(
                            Number(e.target.value)
                          )
                        }
                      />
                    </div>
                  )}

                </div>
              )}
            </Section>

            {/* ERROR */}
            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            {/* SAVE BUTTON */}
            <button
              className="btn btn-primary form-submit"
              disabled={saving}
            >
              {saving
                ? "Saving..."
                : isEditing
                ? "Save Changes"
                : "Generate & Save QR Code"}
            </button>

          </fieldset>
        </form>

        {/* LIVE PREVIEW */}
        <div className="card preview-card">

          <div className="card-title">
            <span className="card-title-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
              </svg>
            </span>

            Live Preview
          </div>

          <div className="qr-preview-box">
            <QRPreview
              qrColor={form.qr_color}
              backgroundColor={form.background_color}
              qrDesign={form.qr_design}
              logoEnabled={form.logo_enabled}
              logoPreviewUrl={logoPreviewUrl}
              logoSize={form.logo_size}
              logoShape={form.logo_shape}
            />
          </div>

          <div className="preview-note">
            This is an approximate preview. The final scannable QR
            code (with your logo embedded) is generated on save.
          </div>

        </div>
      </div>
    </div>
  );
}